import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const RecentlyViewed = ({ currentProductId }) => {
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // Mock recently viewed products
  const mockRecentlyViewed = [
    {
      id: 7,
      name: "Organic Spinach",
      brand: "Green Leaf",
      price: 3.49,
      unit: "5 oz",
      image: "https://images.unsplash.com/photo-1576045057995-568f588f82fb?w=300&h=300&fit=crop",
      rating: 4.3,
      inStock: true
    },
    {
      id: 8,
      name: "Fresh Carrots",
      brand: "Farm Fresh",
      price: 2.99,
      originalPrice: 3.49,
      unit: "2 lbs",
      image: "https://images.unsplash.com/photo-1445282768818-728615cc910a?w=300&h=300&fit=crop",
      rating: 4.5,
      inStock: true,
      discount: 14
    },
    {
      id: 9,
      name: "Organic Tomatoes",
      brand: "Vine Ripe",
      price: 4.99,
      unit: "1 lb",
      image: "https://images.unsplash.com/photo-1546470427-e5ac2d7e2b8e?w=300&h=300&fit=crop",
      rating: 4.7,
      inStock: true
    },
    {
      id: 10,
      name: "Fresh Broccoli",
      brand: "Green Valley",
      price: 3.99,
      unit: "1 head",
      image: "https://images.unsplash.com/photo-1459411621453-7b03977f4bfc?w=300&h=300&fit=crop",
      rating: 4.4,
      inStock: false
    }
  ];

  useEffect(() => {
    // Load recently viewed from localStorage
    const savedRecentlyViewed = localStorage.getItem('recentlyViewed');
    if (savedRecentlyViewed) {
      const parsed = JSON.parse(savedRecentlyViewed);
      setRecentlyViewed(parsed.filter(item => item.id !== currentProductId));
    } else {
      // Use mock data if no localStorage data
      setRecentlyViewed(mockRecentlyViewed.filter(item => item.id !== currentProductId));
    }
  }, [currentProductId]);

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon key={i} name="Star" size={12} className="text-warning fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon key="half" name="StarHalf" size={12} className="text-warning fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon key={`empty-${i}`} name="Star" size={12} className="text-border" />
      );
    }

    return stars;
  };

  const addToCart = (product) => {
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = existingCart.find(item => item.id === product.id);
    
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      existingCart.push({ ...product, quantity: 1 });
    }
    
    localStorage.setItem('cart', JSON.stringify(existingCart));
    
    // Dispatch custom event to update cart count
    window.dispatchEvent(new CustomEvent('cartUpdated'));
  };

  if (recentlyViewed.length === 0) {
    return null;
  }

  return (
    <div className="bg-surface rounded-card shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-heading-semibold text-xl text-text-primary">
            Recently Viewed
          </h3>
          <Button
            variant="ghost"
            onClick={() => {
              localStorage.removeItem('recentlyViewed');
              setRecentlyViewed([]);
            }}
            className="text-text-secondary hover:text-error"
            iconName="Trash2"
            iconSize={16}
          >
            Clear All
          </Button>
        </div>
        <p className="text-text-secondary font-body mt-1">
          Products you've recently viewed
        </p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {recentlyViewed.slice(0, 4).map((product) => (
            <div key={product.id} className="bg-background rounded-card p-3 hover:shadow-card transition-smooth">
              {/* Product Image */}
              <div className="relative mb-3">
                <Link to={`/product-detail-page?id=${product.id}`}>
                  <div className="aspect-square bg-surface rounded-input overflow-hidden">
                    <Image
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </Link>
                
                {/* Discount Badge */}
                {product.discount && (
                  <div className="absolute top-1 left-1 bg-accent text-accent-foreground px-1.5 py-0.5 rounded text-xs font-data font-data-medium">
                    {product.discount}% OFF
                  </div>
                )}

                {/* Stock Status */}
                {!product.inStock && (
                  <div className="absolute inset-0 bg-surface/80 flex items-center justify-center rounded-input">
                    <span className="bg-error text-error-foreground px-2 py-1 rounded text-xs font-data font-data-medium">
                      Out of Stock
                    </span>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="space-y-1">
                <div>
                  <Link to={`/product-detail-page?id=${product.id}`}>
                    <h4 className="font-body-semibold text-sm text-text-primary hover:text-primary transition-smooth line-clamp-2">
                      {product.name}
                    </h4>
                  </Link>
                  <p className="text-xs text-text-secondary font-body">{product.brand}</p>
                </div>

                {/* Rating */}
                <div className="flex items-center space-x-1">
                  <div className="flex space-x-0.5">
                    {renderStars(product.rating)}
                  </div>
                  <span className="text-xs text-text-secondary font-data">
                    {product.rating}
                  </span>
                </div>

                {/* Price */}
                <div className="flex items-center space-x-1">
                  <span className="font-data font-data-medium text-primary text-sm">
                    ${product.price.toFixed(2)}
                  </span>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <span className="text-xs text-text-secondary line-through font-data">
                      ${product.originalPrice.toFixed(2)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-text-secondary font-body">{product.unit}</p>

                {/* Quick Add Button */}
                <Button
                  variant={product.inStock ? "primary" : "outline"}
                  onClick={() => product.inStock && addToCart(product)}
                  disabled={!product.inStock}
                  className="w-full mt-2"
                  iconName="Plus"
                  size="xs"
                >
                  {product.inStock ? "Quick Add" : "Out of Stock"}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* View All Recently Viewed */}
        {recentlyViewed.length > 4 && (
          <div className="text-center mt-4">
            <Button variant="outline" size="sm">
              View All Recently Viewed ({recentlyViewed.length})
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentlyViewed;